/**
 * hours — the non-component half of the ERA 1996 s.8(2)(e) surfaces (0083,
 * module PR 191): the unit conversion, the draft shape, and the validity
 * rules the gates share. The components live in HoursFields.tsx, split for
 * the same react-refresh reason deductions.ts is.
 *
 * THE CONVERSION IS THE WHOLE POINT OF THIS FILE. The wire carries
 * `hoursCenti` — HUNDREDTHS of an hour, an integer, 750 = 7.5 hours. The form
 * takes DECIMAL HOURS to two places, and the two are the same number shifted
 * two places: 7.5 h -> 750 centi-hours. Two decimal places IS centi-hours
 * with a dot in it, so nothing is rounded away and no value expressible in
 * the input is unrepresentable on the wire.
 *
 * WHY DECIMAL AND NOT h:mm. A minutes field would need a base-60 -> base-100
 * conversion that is lossy at most odd minutes (7:20 = 7.333… h = 733.33
 * centi), so the form would silently round a figure the operator typed
 * exactly. Decimal maps without loss, and the conversion is ECHOED beside the
 * field so the operator sees the stored figure rather than trusting it.
 */
import type { PayrollHours } from '../../types'

/** Decimal hours -> centi-hours. Math.round only settles float
 *  representation (7.5 * 100 === 750.0000000000001 in IEEE-754); it never
 *  discards a digit the two-decimal input could express. */
export function hoursToCenti(decimalHours: number): number {
  return Math.round(decimalHours * 100)
}

/** Centi-hours -> the decimal string the input shows. Trailing zeros are
 *  trimmed so a whole number reads "8" rather than "8.00". */
export function centiToHoursText(centi: number): string {
  return String(centi / 100)
}

/** The echo shown beside each amount field, so the stored figure is visible
 *  rather than assumed. Returns '' for an unparseable draft — a half-typed
 *  "7." is not yet a number and should show nothing rather than "NaN". */
export function conversionEcho(amount: string): string {
  if (amount.trim() === '') return ''
  const n = Number(amount)
  if (!Number.isFinite(n) || n < 0) return ''
  return `${n} h = ${hoursToCenti(n)} centi-hours`
}

/** One hours row mid-edit. The amount is the raw STRING typed, as the
 *  deduction and gross fields are: parsing per keystroke fights the input. */
export type HoursDraft = {
  amount: string // decimal hours, e.g. "7.5"
  workType: string
  rate: string // pounds-and-pence per hour, e.g. "12.50"
}

export function emptyHoursDraft(): HoursDraft {
  return { amount: '', workType: '', rate: '' }
}

export function draftFromHours(h: PayrollHours): HoursDraft {
  return {
    amount: centiToHoursText(h.hoursCenti),
    workType: h.workType ?? '',
    rate: h.ratePencePerHour === undefined ? '' : String(h.ratePencePerHour / 100),
  }
}

/** A row is complete when the amount parses non-negative. The LABELS are
 *  optional — s.8(2)(e) requires only the number, and a row with neither
 *  label IS the lawful (i) single-aggregate presentation. This is the
 *  deliberate opposite of a deduction row, where purpose is required. */
export function hoursDraftComplete(d: HoursDraft): boolean {
  const n = Number(d.amount)
  if (d.amount.trim() === '' || !Number.isFinite(n) || n < 0) return false
  if (d.rate.trim() !== '') {
    const r = Number(d.rate)
    if (!Number.isFinite(r) || r < 0) return false
  }
  return true
}

export function hoursDraftsValid(ds: HoursDraft[]): boolean {
  return ds.every(hoursDraftComplete)
}

/** Drafts -> wire shape. Labels are omitted entirely when blank, never sent
 *  as "" — an empty string would be a label that labels nothing, and the
 *  server's CHECK refuses it by name. */
export function draftsToHours(ds: HoursDraft[]): PayrollHours[] {
  return ds.map(d => {
    const out: PayrollHours = { hoursCenti: hoursToCenti(Number(d.amount)) }
    const wt = d.workType.trim()
    if (wt !== '') out.workType = wt
    if (d.rate.trim() !== '') out.ratePencePerHour = Math.round(Number(d.rate) * 100)
    return out
  })
}

export function totalCenti(ds: HoursDraft[]): number {
  return ds.reduce((sum, d) => sum + (hoursDraftComplete(d) ? hoursToCenti(Number(d.amount)) : 0), 0)
}

/**
 * THE EITHER/OR the gates enforce, mirroring the wire's conditional
 * tri-state. For a time-varying worker SILENCE IS IMPOSSIBLE — the server
 * answers an omitted field with a named 400 — so the form must make the
 * operator answer before it will submit:
 *
 *   'unanswered' -> submit disabled. The initial state, deliberately.
 *   'none'       -> sends []  ("no variable hours this period")
 *   'rows'       -> sends the list
 *
 * There is no fourth state and no default: defaulting to 'none' would send
 * an answer the operator never gave, which is the same silent
 * characterisation the profile flag's radio pair exists to prevent.
 */
export type HoursAnswer = 'unanswered' | 'none' | 'rows'

/** What the gate should send, given the answer and the rows. Returns
 *  undefined to mean OMIT THE FIELD — used only when the worker is not
 *  time-varying, where supplying anything (even []) earns a named refusal. */
export function hoursBodyFor(
  flagTrue: boolean,
  answer: HoursAnswer,
  drafts: HoursDraft[],
): PayrollHours[] | undefined {
  if (!flagTrue) return undefined
  if (answer === 'none') return []
  if (answer === 'rows') return draftsToHours(drafts)
  return undefined // 'unanswered' — the caller must not submit
}
